import React, { createContext, useReducer, useCallback, useMemo } from "react";
import axios from "../../axios-orders";
import { USER_COMPANY_TYPES, USER_TYPES } from "../../utils/enums";
import { ABILITIES } from "../../utils/abilities";
import { UserContextType, UserState, ProviderProps } from "../types";

// Initial state
const initialState: UserState = {
  user: null,
  isLoading: false,
  error: false,
  companyChangeStatus: {
    status: null,
  },
  userLanguageChangeStatus: null,
  companyList: [],
  abilities: new Set<string>(),
  purchaseOrderModuleEnabled: false,
};

// Create context
export const UserContext = createContext<UserContextType>({
  ...initialState,
  getUser: async () => {},
  changeCompany: async () => {},
  changeUserLanguage: async () => {},

  // Computed properties
  isSeller: false,
  isBuy: false,
  isNew: false,
  isResetPassword: false,
  isPending: false,
  isBlocked: false,
  isPendingAdminApproval: false,
  isPendingRegistration: false,
  isCompliant: false,

  // Company type properties
  isAdmin: false,
  isBuyer: false,
  isManager: false,
  isAnalyst: false,
  isDefault: false,
  isVisualizer: false,
  isCommercial: false,
  isRequester: false,
  canManagePeople: false,
  can: () => false,
});

// User reducer
const userReducer = (state: UserState, action: any): UserState => {
  switch (action.type) {
    case "GET_USER_START":
      return {
        ...state,
        isLoading: true,
        error: false,
      };
    case "GET_USER_SUCCESS":
      return {
        ...state,
        user: action.user,
        companyList: action.companyList ?? state.companyList,
        abilities: action.abilities ?? state.abilities,
        purchaseOrderModuleEnabled:
          action.purchaseOrderModuleEnabled ?? state.purchaseOrderModuleEnabled,
        isLoading: false,
        error: false,
      };
    case "GET_USER_FAILED":
      return {
        ...state,
        error: true,
        isLoading: false,
      };
    case "CHANGE_USER_LANGUAGE_START":
      return {
        ...state,
        isLoading: true,
        error: false,
      };
    case "CHANGE_USER_LANGUAGE_SUCCESS":
      return {
        ...state,
        user: action.user,
        userLanguageChangeStatus: action.user,
        isLoading: false,
        error: false,
      };
    case "CHANGE_USER_LANGUAGE_FAILED":
      return {
        ...state,
        error: true,
        isLoading: false,
      };
    case "CHANGE_COMPANY_START":
      return {
        ...state,
        isLoading: true,
        error: false,
      };
    case "CHANGE_COMPANY_SUCCESS":
      return {
        ...state,
        user: action.user,
        companyChangeStatus: action.user,
        isLoading: false,
        error: false,
      };
    case "CHANGE_COMPANY_FAILED":
      return {
        ...state,
        error: true,
        isLoading: false,
      };
    default:
      return state;
  }
};

// Provider component
export const UserProvider: React.FC<ProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  // Get user action
  const getUser = useCallback(async () => {
    dispatch({ type: "GET_USER_START" });

    try {
      const response = await axios.get("/user/me");
      const resData = response.data;

      if (resData.errors) {
        dispatch({
          type: "GET_USER_FAILED",
          error: resData.errors,
        });
        return;
      }

      dispatch({
        type: "GET_USER_SUCCESS",
        user: resData.user,
        companyList: resData.companyList ?? [],
        // The backend serializes its Set<String> of effective abilities as a JSON array.
        abilities: new Set<string>(resData.abilities ?? []),
        purchaseOrderModuleEnabled: !!resData.purchaseOrderModuleEnabled,
      });
    } catch (error) {
      dispatch({
        type: "GET_USER_FAILED",
        error,
      });
    }
  }, []);

  // Change company action
  const changeCompany = useCallback(async (companyId: string) => {
    dispatch({ type: "CHANGE_COMPANY_START" });

    try {
      const response = await axios.get(`/user/company/${companyId}`);

      if (response.data && response.data.errors) {
        dispatch({
          type: "CHANGE_COMPANY_FAILED",
          error: response.data.errors,
        });
        return;
      }

      dispatch({
        type: "CHANGE_COMPANY_SUCCESS",
        user: response.data,
      });
    } catch (error) {
      dispatch({
        type: "CHANGE_COMPANY_FAILED",
        error,
      });
    }
  }, []);

  // Change user language action
  const changeUserLanguage = useCallback(
    async (userId: string, language: string) => {
      dispatch({ type: "CHANGE_USER_LANGUAGE_START" });

      try {
        const response = await axios.put(`/user/${userId}/${language}`);

        if (response.data && response.data.errors) {
          dispatch({
            type: "CHANGE_USER_LANGUAGE_FAILED",
            error: response.data.errors,
          });
          return;
        }

        dispatch({
          type: "CHANGE_USER_LANGUAGE_SUCCESS",
          user: response.data,
        });
      } catch (error) {
        dispatch({
          type: "CHANGE_USER_LANGUAGE_FAILED",
          error,
        });
        throw error;
      }
    },
    []
  );

  // Computed properties
  const userData = state?.user;
  const abilities = state.abilities;

  // Effective-ability check. False (never throws) while loading or for an empty set.
  const can = useCallback(
    (ability: string) => abilities.has(ability),
    [abilities]
  );

  const computedValues = useMemo(() => {
    const isAdmin = userData?.userCompanyType === USER_COMPANY_TYPES.ADMIN;
    const isManager = userData?.userCompanyType === USER_COMPANY_TYPES.MANAGER;

    return {
      isSeller: userData?.type === USER_TYPES.SELLER,
      isBuy: userData?.type === USER_TYPES.BUYER,
      isNew: userData?.role === USER_TYPES.NEW,
      isResetPassword: userData?.role === USER_TYPES.RESET_PASSWORD,
      isPending: userData?.role === USER_TYPES.PENDING,
      isBlocked: userData?.role === USER_TYPES.BLOCKED,
      isPendingAdminApproval: userData?.role === USER_TYPES.ADMIN_APPROVAL,
      isPendingRegistration: userData?.role === USER_TYPES.PENDING_REGISTRATION,
      isCompliant: !!userData?.compliant,

      // Company type properties
      isAdmin,
      isBuyer: userData?.userCompanyType === USER_COMPANY_TYPES.BUYER,
      isManager,
      isAnalyst: userData?.userCompanyType === USER_COMPANY_TYPES.ANALYST,
      isDefault: userData?.userCompanyType === USER_COMPANY_TYPES.DEFAULT,
      isVisualizer: userData?.userCompanyType === USER_COMPANY_TYPES.VISUALIZER,
      isCommercial: userData?.userCompanyType === USER_COMPANY_TYPES.COMMERCIAL,
      isRequester: userData?.userCompanyType === USER_COMPANY_TYPES.REQUESTER,

      // Tier-2 ability: "may this viewer change someone else's role or activation". Driven by
      // the backend-computed effective abilities (ADMINISTER_USERS on the buyer side,
      // SELLER_ADMINISTER_USERS on the seller side), the same abilities the backend's
      // UserMutationGuard checks — NOT by the viewer's role boolean, so a custom role granted the
      // ability gets the controls and an admin-typed user stripped of it does not. One place
      // computes this so EditUserForm's role selector and User.tsx's Approve button can't drift
      // into checking two different conditions for the same rule.
      canManagePeople:
        can(ABILITIES.ADMINISTER_USERS) ||
        can(ABILITIES.SELLER_ADMINISTER_USERS),
    };
  }, [userData, can]);

  return (
    <UserContext.Provider
      value={{
        ...state,
        getUser,
        changeCompany,
        changeUserLanguage,
        ...computedValues,
        can,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
